import type { ListParams, SubjectType } from '@/types';
import type { AnalyticsQuery } from '@/types';

export const QUERY_ENTITIES = [
  'users',
  'teams',
  'pipelines',
  'companies',
  'contacts',
  'leads',
  'deals',
  'activities',
  'tasks',
  'conversations',
  'calls',
  'customFields',
  'tags',
  'savedViews',
  'auditEvents',
  'notifications',
  'agentes',
  'tickets',
  'escalamientos',
  'automatizaciones',
  'integraciones',
  // Las claves de la extensión de Gmail. Aparte de `integraciones` y no bajo
  // ella: son de QUIEN MIRA y no de la organización, así que una invalidación
  // del registro de conexiones —que sí es de todos— no tiene por qué volver a
  // pedirlas, ni al revés.
  'extensionClaves',
  'correos',
] as const;
export type QueryEntity = (typeof QUERY_ENTITIES)[number];

/**
 * One factory for every cache key. Two views that build their own keys by hand
 * will disagree eventually, and the bug shows up as stale data nobody can
 * reproduce. Every key starts with the tenant, so switching tenants can never
 * serve another organisation's cached rows.
 */
export const queryKeys = {
  /**
   * Null tenant is a real key: it is the anonymous, pre-sign-in session, and it
   * is the one key the login screen reads. Nothing that describes a signed-in
   * person may ever be written there — whatever sits under it is served to
   * whoever sits down next, before they have authenticated.
   */
  session: (tenantId: string | null) => ['session', tenantId] as const,
  /**
   * Every session key at once, whichever organisation it was filed under.
   * Signing out and an expired session both drop this whole subtree: one
   * snapshot left behind is the previous account's data on the next screen.
   */
  sessions: () => ['session'] as const,
  tenants: () => ['tenants'] as const,
  tenant: (tenantId: string) => ['tenants', tenantId] as const,
  scope: (tenantId: string) => ['tenant', tenantId] as const,
  list: (tenantId: string, entity: QueryEntity, params?: ListParams) =>
    ['tenant', tenantId, entity, 'list', params ?? {}] as const,
  detail: (tenantId: string, entity: QueryEntity, id: string) =>
    ['tenant', tenantId, entity, 'detail', id] as const,
  /** Lo que hay en un embudo, contado por el servidor; lo invalida quien mueve una tarjeta. */
  pipelineUsage: (tenantId: string, pipelineId: string) =>
    ['tenant', tenantId, 'pipelines', 'usage', pipelineId] as const,
  /**
   * Cuánta gente tiene cada cuenta de UNA página de Empresas.
   *
   * Bajo la rama de `companies` —así una invalidación del módulo la arrastra—
   * y con su propio segmento: no es una lista de empresas, es lo medido sobre
   * ellas, y apilarla bajo `list` serviría una página de filas donde se espera
   * una de conteos.
   *
   * Los ids entran en la clave porque SON la pregunta: la página siguiente
   * pregunta por otras cuentas y tiene que ser otra entrada, o la segunda
   * página dibujaría los números de la primera.
   */
  companyRollups: (tenantId: string, companyIds: readonly string[]) =>
    ['tenant', tenantId, 'companies', 'rollups', companyIds] as const,
  timeline: (tenantId: string, subjectType: SubjectType, subjectId: string, params?: ListParams) =>
    ['tenant', tenantId, 'activities', 'timeline', subjectType, subjectId, params ?? {}] as const,
  /**
   * El historial de una PERSONA. Va bajo su propia rama y no bajo `activities`
   * porque no es la misma lista: aquélla es de una ficha de embudo y ésta de
   * quien está detrás, y una invalidación de una no puede arrastrar a la otra.
   *
   * Sin el cursor adentro: las páginas siguientes se apilan en esta misma
   * entrada, así que meterlo acá mintaría una entrada por página y volver a la
   * ficha obligaría a bajar todo de nuevo.
   */
  historial: (tenantId: string, personaId: string) =>
    ['tenant', tenantId, 'personas', 'historial', personaId] as const,
  historialPorTipo: (tenantId: string, personaId: string, type: string) =>
    ['tenant', tenantId, 'personas', 'historial', personaId, 'tipo', type] as const,
  /**
   * Desde qué número de WhatsApp se le puede contestar a una persona, y hasta
   * cuándo. Rama propia: cambia con el reloj —la ventana de 24 horas se cierra
   * sola— y con cada mensaje que entra, a un ritmo que no es el del historial.
   */
  whatsappDePersona: (tenantId: string, personaId: string) =>
    ['tenant', tenantId, 'personas', 'whatsapp', personaId] as const,
  /**
   * La agenda que un WhatsApp conectado dejó guardada, contada.
   *
   * Bajo `integraciones` y FUERA de su `list`, a propósito: contarla le cuesta
   * al servidor una pregunta por cada agendado, y colgada de la lista se
   * volvería a pedir cada vez que alguien conecta o desconecta otra cosa. Se
   * invalida sola, cuando se depura.
   */
  agendaDeWhatsapp: (tenantId: string, conexionId: string) =>
    ['tenant', tenantId, 'integraciones', 'agenda', conexionId] as const,
  /**
   * La lista de Llamadas, sea de la organización o de una ficha.
   *
   * Rama propia y no `list(tenantId, 'calls', …)` porque no es la misma lista:
   * aquélla son `Call` del modelo viejo y ésta son `LlamadaVista`, que es lo que
   * una llamada ES sin importar de dónde salió. Bajo una sola clave, una
   * organización que cambia de fuente serviría filas de la forma anterior.
   *
   * Sin el cursor adentro: las páginas siguientes se apilan en esta misma
   * entrada, así que meterlo acá inventaría una entrada por página y volver a la
   * pantalla obligaría a bajar todo de nuevo.
   */
  llamadasScope: (tenantId: string) => ['tenant', tenantId, 'llamadas'] as const,
  llamadas: (tenantId: string, pregunta: readonly string[]) =>
    ['tenant', tenantId, 'llamadas', ...pregunta] as const,
  /**
   * Lo que le falta a UNA llamada, pedido aparte de la lista que la trajo.
   *
   * Bajo la misma rama —`invalidateCalls` tira las dos de un saque, y una lista
   * refrescada con un detalle viejo al lado sería la contradicción que esa
   * función existe para evitar— y con un segmento propio, `una`, para que no se
   * pueda confundir con una lista cuya pregunta tuviera dos partes.
   *
   * Por id y no por la fila entera: la respuesta depende de cuál llamada es y de
   * nada más, así que dos filas del mismo hecho comparten la entrada en vez de
   * pedir dos veces lo mismo.
   */
  unaLlamada: (tenantId: string, llamadaId: string) =>
    ['tenant', tenantId, 'llamadas', 'una', llamadaId] as const,
  /**
   * Las reuniones del calendario, sean de la organización o de una persona.
   *
   * Rama propia por lo mismo que `llamadas`: no es una lista paginada de un
   * recurso del CRM sino la ventana que el servidor decide, y `reunionesScope`
   * existe para invalidarlas TODAS de un saque — el interruptor «Grabar» de una
   * reunión cambia la misma fila en la pantalla de Calendario y en la ficha de
   * cada invitado, y las dos tienen que enterarse.
   *
   * `pregunta` es la ventana pedida —la mitad del calendario y la persona—:
   * apilar dos preguntas distintas bajo la misma clave mostraría las pasadas
   * dentro de «Próximas».
   */
  reunionesScope: (tenantId: string) => ['tenant', tenantId, 'reuniones'] as const,
  reuniones: (tenantId: string, pregunta: readonly string[]) =>
    ['tenant', tenantId, 'reuniones', ...pregunta] as const,
  /**
   * Google Drive: el estado de la integración y los documentos de cada ficha.
   *
   * Una rama propia, con `drive` como raíz de las dos cosas, porque conectar o
   * desconectar la carpeta las cambia juntas: el estado pasa a decir otra cosa y
   * las listas de documentos dejan de valer —o empiezan a existir—. Invalidar
   * `drive(tenantId)` las tira todas de un saque.
   *
   * `driveEstado` la comparten Ajustes › Integraciones y la compuerta de cada
   * ficha (`useDriveActivo`): abrir una empresa después de mirar Integraciones
   * no vuelve a preguntar.
   *
   * `documentos` lleva de quién son —`empresa` o `persona`— además del id: las
   * dos rutas contestan listas distintas, y una empresa y una persona con el
   * mismo id no pueden compartir entrada.
   */
  drive: (tenantId: string) => ['tenant', tenantId, 'drive'] as const,
  driveEstado: (tenantId: string) => ['tenant', tenantId, 'drive', 'estado'] as const,
  documentos: (tenantId: string, de: 'empresa' | 'persona', id: string) =>
    ['tenant', tenantId, 'drive', 'documentos', de, id] as const,
  /**
   * El estado del conector a HubSpot de la organización, que es lo único que el
   * conector tiene para consultar. Bajo su propia rama, como el de Drive.
   *
   * LO QUE ENTRA ACÁ ES EL ESTADO Y NADA MÁS. El token con que se conecta no es
   * una consulta ni la variable de una: vive en el formulario y muere al
   * enviarse. El estado trae sólo su pista.
   */
  hubspotEstado: (tenantId: string) => ['tenant', tenantId, 'hubspot', 'estado'] as const,
  messages: (tenantId: string, conversationId: string, params?: ListParams) =>
    ['tenant', tenantId, 'conversations', 'messages', conversationId, params ?? {}] as const,
  unreadCount: (tenantId: string, userId: string) =>
    ['tenant', tenantId, 'notifications', 'unread', userId] as const,
  analytics: (tenantId: string, report: string, query: AnalyticsQuery) =>
    ['tenant', tenantId, 'analytics', report, query] as const,
  /**
   * El resumen se cachea por PERÍODO y no por el objeto de rango: sus tres
   * valores son un nombre cada uno, y volver a «Hoy» tiene que encontrar lo que
   * ya se había traído.
   */
  resumen: (tenantId: string, rango: string) => ['tenant', tenantId, 'resumen', rango] as const,
  uso: (tenantId: string, rango: string, agente: string | null = null) =>
    ['tenant', tenantId, 'uso', rango, agente ?? 'todos'] as const,
  bases: (tenantId: string) => ['tenant', tenantId, 'bases'] as const,
  /**
   * El historial de corridas de UN agente, para Trazabilidad.
   *
   * Bajo su propia rama y no `list(tenantId, 'agentes', …)` porque no es la
   * misma lista: aquélla es la vitrina —qué agentes tiene la organización— y
   * ésta lo que hizo uno de ellos. Bajo una sola clave, refrescar la pantalla
   * de Agentes tiraría el historial y al revés.
   */
  corridas: (tenantId: string, agenteId: string, filtros: { periodo: string; estados: readonly string[] }) =>
    ['tenant', tenantId, 'agentes', 'corridas', agenteId, 'lista', filtros] as const,
  corrida: (tenantId: string, agenteId: string, corridaId: string) =>
    ['tenant', tenantId, 'agentes', 'corridas', agenteId, 'una', corridaId] as const,
  corridaCapturas: (tenantId: string, agenteId: string, corridaId: string) =>
    ['tenant', tenantId, 'agentes', 'corridas', agenteId, 'una', corridaId, 'capturas'] as const,
  formularioDeLlamada: (tenantId: string, agenteId: string) =>
    ['tenant', tenantId, 'agentes', 'probar-llamada', agenteId] as const,
  /**
   * La pantalla de UN agente residente: su cabecera, sus días, sus
   * despertadores, sus corridas y su carpeta. Una rama propia y aparte de
   * `corridas` (Trazabilidad): son otras lecturas, del residente y no de la
   * plataforma, y refrescar una no tiene por qué tirar la otra.
   */
  residente: (tenantId: string, agenteId: string, ...resto: readonly unknown[]) =>
    ['tenant', tenantId, 'agentes', 'residente', agenteId, ...resto] as const,
  search: (tenantId: string, term: string) => ['tenant', tenantId, 'search', term] as const,
  /**
   * Toda la rama de Escalamiento Humano: la cola, sus conteos, cada caso y su
   * historia. Después de cualquier acción sobre un caso se invalida entera,
   * porque un cambio de estado mueve el caso de vista y cambia el número del
   * menú — y un duplicado escribe en OTRO caso.
   */
  escalamientos: (tenantId: string) => ['tenant', tenantId, 'escalamientos'] as const,
  automatizaciones: (tenantId: string) => ['tenant', tenantId, 'automatizaciones'] as const,
  softphone: (tenantId: string) => ['tenant', tenantId, 'softphone'] as const,
} as const;

/**
 * Explicit staleness per data shape — the architecture forbids relying on the
 * library default. Reference data barely moves; a shared inbox moves constantly.
 */
export const STALE_TIME = {
  reference: 10 * 60_000,
  collection: 60_000,
  detail: 30_000,
  analytics: 5 * 60_000,
  realtime: 10_000,
} as const;
